import axios from 'axios';
import User from '../types/user.type';
import jwt_decode, { JwtPayload } from 'jwt-decode';

const API_URL = process.env.REACT_APP_BACKEND_URL ?? '';

if (!API_URL) {
  console.warn('REACT_APP_BACKEND_URL is not defined. API calls may fail.');
}

export interface TokenModal {
  user_id: string;
  isAdmin: boolean;
}

export async function register(email: string): Promise<any> {
  const accessToken = JSON.parse(localStorage.getItem('accessToken') || 'null');
  try {
    const response = await axios.post(
      `${API_URL}/api/register`,
      { email },
      {
        headers: {
          Authorization: `JWT ${accessToken}`,
        },
      },
    );
    return response;
  } catch (err: unknown) {
    if (axios.isAxiosError(err) && err.response?.data?.email) {
      return new Error(err.response.data.email);
    }
    return new Error('Token has expired.');
  }
}

export async function validateToken(token: string): Promise<any> {
  try {
    const response = await axios.post(
      `${API_URL}/api/password_reset/validate_token/`,
      { token },
    );
    return response;
  } catch (err: unknown) {
    if (axios.isAxiosError(err) && err.response) {
      if (err.response.status === 400) {
        return new Error('Your password reset link has expired. Please request a new one.');
      } else if (err.response.status === 404) {
        return new Error('Invalid or expired password reset link. Please request a new one.');
      }
      return new Error(err.response.data.detail || 'Invalid reset link. Please try again.');
    }
    return new Error('Network error. Please check your connection and try again.');
  }
}

export async function confirmResetPassword(token: string, password: string): Promise<any> {
  try {
    const response = await axios.post(
      `${API_URL}/api/password_reset/confirm/`,
      { password, token },
    );
    return response;
  } catch (err: unknown) {
    if (axios.isAxiosError(err) && err.response) {
      if (err.response.status === 400) {
        if (err.response.data?.password) {
          const passwordErrors = err.response.data.password;
          if (Array.isArray(passwordErrors) && passwordErrors.length > 0) {
            return new Error(`Password requirements: ${passwordErrors.join(', ')}`);
          } else if (typeof passwordErrors === 'string') {
            return new Error(`Password error: ${passwordErrors}`);
          }
        }
        return new Error(err.response.data.detail || 'Please ensure your password meets the requirements.');
      } else if (err.response.status === 404) {
        return new Error('Password reset link has expired. Please request a new one.');
      } else if (err.response.status === 410) {
        return new Error('This password reset link has already been used. Please request a new one.');
      }
      return new Error(err.response.data.detail || 'Failed to reset password. Please try again.');
    }
    return new Error('Network error. Please check your connection and try again.');
  }
}

export async function forgotPassword(email: string): Promise<any> {
  try {
    const response = await axios.post(`${API_URL}/api/password_reset/`, { email });
    return response;
  } catch (err: unknown) {
    if (axios.isAxiosError(err) && err.response) {
      if (err.response.status === 400) {
        if (err.response.data?.email) {
          return new Error(err.response.data.email);
        }
        return new Error('Please enter a valid email address.');
      } else if (err.response.status === 404) {
        return new Error('Email address not found. Please check your email or contact your administrator.');
      } else if (err.response.status === 429) {
        return new Error('Too many reset attempts. Please wait a few minutes before trying again.');
      }
      return new Error(err.response.data.detail || 'Failed to send reset email. Please try again.');
    }
    return new Error('Network error. Please check your connection and try again.');
  }
}

export const changePassword = (old_password: string, new_password: string): Promise<any> => {
  return axios.post(`${API_URL}/api/change_password/`, {
    old_password,
    new_password,
  });
};

export async function login(email: string, password: string): Promise<User | Error> {
  let user: User = {
    isLoggedIn: false,
    name: '',
    isAdmin: false,
  };

  try {
    const response = await axios.post(`${API_URL}/api/login`, { email, password });

    localStorage.setItem('accessToken', JSON.stringify(response.data.access));
    localStorage.setItem('refreshToken', JSON.stringify(response.data.refresh));
    localStorage.setItem('userEmail', email);

    const decoded = jwt_decode<TokenModal>(response.data.access || '') || null;

    user = {
      isLoggedIn: true,
      name: decoded.user_id,
      isAdmin: decoded.isAdmin,
    };
  } catch (err: unknown) {
    if (axios.isAxiosError(err)) {
      if (err.response?.status === 401) {
        return new Error('Invalid email or password. Please check your credentials and try again.');
      } else if (err.response?.status === 400) {
        const errorDetail = err.response.data.detail || err.response.data.error || err.response.data.message || '';
        const errorString = typeof errorDetail === 'string' ? errorDetail : JSON.stringify(errorDetail);
        const fullResponseString = JSON.stringify(err.response.data);

        if (errorString.includes('Staff users are not allowed to log in') ||
            fullResponseString.includes('Staff users are not allowed to log in')) {
          return new Error('This account does not have permission to access the web application. Please contact your administrator.');
        }

        if (err.response.data.detail) {
          return new Error(err.response.data.detail);
        }

        const fieldErrors = Object.values(err.response.data).filter(val => Array.isArray(val)) as string[][];
        if (fieldErrors.length > 0 && fieldErrors[0].length > 0) {
          return new Error(fieldErrors[0][0]);
        }

        return new Error('Please check your email and password format.');
      } else if (err.response?.status === 429) {
        return new Error('Too many login attempts. Please wait a few minutes before trying again.');
      } else if (err.response?.status === 500) {
        return new Error('Server error. Please try again later.');
      } else if (err.response?.status === 503) {
        return new Error('Service temporarily unavailable. Please try again later.');
      }
      return new Error(err.response?.data?.detail || 'Login failed. Please check your credentials and try again.');
    }
    return new Error('Network error. Please check your connection and try again.');
  }
  return user;
}

export async function refreshToken(): Promise<string> {
  let accessToken = '';
  try {
    const storedRefreshToken = localStorage.getItem('refreshToken')
      ? JSON.parse(localStorage.getItem('refreshToken') || '')
      : '';
    const response = await axios.post(`${API_URL}/api/token/refresh`, {
      refresh: storedRefreshToken,
    });
    localStorage.setItem('accessToken', JSON.stringify(response.data.access));
    localStorage.setItem('refreshToken', JSON.stringify(response.data.refresh));
    accessToken = response.data.access;
  } catch (err: unknown) {
    console.error('Token refresh failed:', err);
  }
  return accessToken;
}

export function logout(): void {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userEmail');
}

export async function getCurrentUser(): Promise<User> {
  const accessToken = localStorage.getItem('accessToken');
  const storedRefreshToken = localStorage.getItem('refreshToken');

  if (!accessToken || !storedRefreshToken) {
    return { isLoggedIn: false, name: '', isAdmin: false };
  }

  try {
    const accessTokenDecoded = jwt_decode<JwtPayload>(JSON.parse(accessToken)) || null;
    const refreshTokenDecoded = jwt_decode<JwtPayload>(JSON.parse(storedRefreshToken)) || null;
    const userToken = jwt_decode<TokenModal>(JSON.parse(accessToken)) || null;

    const isAccessTokenValid = accessTokenDecoded?.exp ? Date.now() < accessTokenDecoded.exp * 1000 : false;
    const isRefreshTokenValid = refreshTokenDecoded?.exp ? Date.now() < refreshTokenDecoded.exp * 1000 : false;

    let user: User = { isLoggedIn: false, name: '', isAdmin: false };

    if (isAccessTokenValid) {
      user.isLoggedIn = true;
      user.isAdmin = userToken?.isAdmin || false;
      user.name = userToken?.user_id || '';
    } else if (isRefreshTokenValid) {
      const newAccessToken = await refreshToken();
      if (newAccessToken) {
        const newUserToken = jwt_decode<TokenModal>(newAccessToken) || null;
        user.isLoggedIn = true;
        user.isAdmin = newUserToken?.isAdmin || false;
        user.name = newUserToken?.user_id || '';
      }
    }

    return user;
  } catch (err: unknown) {
    console.error('Error getting current user:', err);
    return { isLoggedIn: false, name: '', isAdmin: false };
  }
}

export async function getCurrentUserWithIdentification(): Promise<User> {
  const userEmail = localStorage.getItem('userEmail') || '';
  const user = await getCurrentUser();

  if (user.isLoggedIn && !user.name) {
    user.name = userEmail;
  }

  return user;
}
